import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  MessageCircle,
  Bot,
  RefreshCw,
  Settings,
  ArrowUp,
  ArrowDown,
  X,
  Info,
  Archive,
  Ban,
  ShieldCheck,
} from "lucide-react";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import Pagination from "@/components/ui/Pagination";
import { MultiSelect } from "@/components/ui/multi-select-combobox";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AiSessionChatModal from "@/components/AiSessionChatModal";

type ChatStage =
  | "UNDERSTANDING_STUDENT_PROFILE"
  | "EXPLORING_COURSE_AND_CAREER_OPTIONS"
  | "EVALUATING_COLLEGE_CHOICES"
  | "ADMISSION_DECISION_AND_ENROLLMENT"
  | "ARCHIVED";

interface AiChatSession {
  id: string;
  studentId: string;
  status: string;
  isManualMode: boolean;
  isBlocked?: boolean;
  blockReason?: string | null;
  channel: "web" | "whatsapp";
  startedAt: string;
  stage: ChatStage | null;
  stageUpdatedAt: string | null;
  manualChatStage: ChatStage | null;
  assigneeId: string | null;
  assignee: { id: string; name: string } | null;
  student: { id: string; name: string; phoneNumber: string } | null;
  latestMessage: { message: string; sentAt: string; senderId: string } | null;
  lastStudentMessageAt: string | null;
}

interface AdminUser {
  id: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
}

type SortBy = "activity" | "stage" | "manualStage" | "assignee";
type SortOrder = "asc" | "desc";

// Badge colours + description per stage. The displayed label is the exact enum
// value with underscores replaced by spaces (see stageLabel).
const STAGE_META: Record<ChatStage, { className: string; description: string }> = {
  UNDERSTANDING_STUDENT_PROFILE: {
    className: "bg-blue-100 text-blue-700",
    description:
      "Getting to know the student — grade, stream, scores, interests, family context and constraints. Still gathering who the student is.",
  },
  EXPLORING_COURSE_AND_CAREER_OPTIONS: {
    className: "bg-indigo-100 text-indigo-700",
    description:
      "Discussing what to study and which career paths fit — comparing fields, courses and career outcomes. The profile is broadly known; now weighing directions.",
  },
  EVALUATING_COLLEGE_CHOICES: {
    className: "bg-amber-100 text-amber-700",
    description:
      "Comparing specific colleges/universities — rankings, location, fees, cut-offs and placements. A course direction is set; now choosing where.",
  },
  ADMISSION_DECISION_AND_ENROLLMENT: {
    className: "bg-green-100 text-green-700",
    description:
      "Acting on a decision — applications, deadlines, entrance exams, documents, fees, accepting an offer and enrollment steps.",
  },
  ARCHIVED: {
    className: "bg-gray-200 text-gray-700",
    description: "Dead / inactive / not interested lead.",
  },
};

// Stages in journey order — for the filter dropdown, the legend, and step numbers.
const STAGE_ORDER: ChatStage[] = [
  "UNDERSTANDING_STUDENT_PROFILE",
  "EXPLORING_COURSE_AND_CAREER_OPTIONS",
  "EVALUATING_COLLEGE_CHOICES",
  "ADMISSION_DECISION_AND_ENROLLMENT",
];

// 1-based step number of a stage (e.g. UNDERSTANDING_STUDENT_PROFILE → 1).
const stageStep = (s: ChatStage) => STAGE_ORDER.indexOf(s) + 1;

// Exact stage value with a step prefix, underscores removed
// (e.g. "Step 1: UNDERSTANDING STUDENT PROFILE").
const stageLabel = (s: ChatStage) => `Step ${stageStep(s)}: ${s.replace(/_/g, " ")}`;

// Short codes for table-row badges — A1-A4 for A Stage (AI), C1-C4 for C
// Stage (CSM/manual). Same 4 underlying values, just a terser display.
// ARCHIVED isn't in STAGE_ORDER, so stageStep() returns 0 for it — A0/C0.
const aStageLabel = (s: ChatStage) => `A${stageStep(s)}`;
const cStageLabel = (s: ChatStage) => `C${stageStep(s)}`;

// C Stage dropdown options, in the order the user wants them shown: C0-C4.
const MANUAL_STAGE_OPTIONS: ChatStage[] = ["ARCHIVED", ...STAGE_ORDER];

const PAGE_SIZE = 20;

const adminLabel = (a: AdminUser) =>
  a.name || a.email || a.phoneNumber || a.id;


const isOutside24hWindow = (s: {
  channel: "web" | "whatsapp";
  lastStudentMessageAt: string | null;
}) =>
  s.channel === "whatsapp" &&
  (!s.lastStudentMessageAt ||
    Date.now() - new Date(s.lastStudentMessageAt).getTime() > 24 * 60 * 60 * 1000);

const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    className={cn(
      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none",
      checked ? "bg-orange-500" : "bg-gray-300",
      disabled && "opacity-50 cursor-not-allowed",
    )}
  >
    <span
      className={cn(
        "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
        checked ? "translate-x-6" : "translate-x-1",
      )}
    />
  </button>
);

const AiSessions: React.FC = () => {
  const { isSuperAdmin, isCsm, user } = useAuth();
  const [sessions, setSessions] = useState<AiChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterManual, setFilterManual] = useState<"all" | "manual" | "ai">("all");
  const [filterAStage, setFilterAStage] = useState<"all" | ChatStage>("all");
  const [filterCStage, setFilterCStage] = useState<"all" | ChatStage>("all");
  const [filterAssignee, setFilterAssignee] = useState<string>("all");
  const [showStageInfo, setShowStageInfo] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeLeadsCount, setActiveLeadsCount] = useState<number | null>(null);
  const [selectedSession, setSelectedSession] = useState<AiChatSession | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>("activity");
  const [order, setOrder] = useState<SortOrder>("desc");
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [savingAssigneeId, setSavingAssigneeId] = useState<string | null>(null);
  const [savingManualStageId, setSavingManualStageId] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchSessions = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(page),
        limit: String(PAGE_SIZE),
        sortBy,
        order,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterManual === "manual") params.isManualMode = "true";
      if (filterManual === "ai") params.isManualMode = "false";
      if (filterAStage !== "all") params.stage = filterAStage;
      if (filterCStage !== "all") params.manualStage = filterCStage;
      if (filterAssignee !== "all") params.assigneeId = filterAssignee;
      if (showArchived) params.archived = "true";

      const query = new URLSearchParams(params).toString();
      const data = await api.get(`/admin/ai-chat-sessions?${query}`);
      const newTotalPages = data.totalPages ?? 1;
      // If a background refresh finds the current page no longer exists
      // (e.g. another admin archived leads out from under it), snap back to
      // the last valid page instead of silently showing an empty table.
      if (page > newTotalPages) {
        setPage(newTotalPages);
        return;
      }
      setSessions(data.items ?? []);
      setTotalPages(newTotalPages);
      setTotal(data.total ?? 0);
    } catch {
      // A background refresh failing shouldn't wipe what's already on screen.
      if (!silent) setSessions([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, debouncedSearch, filterManual, filterAStage, filterCStage, filterAssignee, showArchived, sortBy, order]);

  useEffect(() => {
    void fetchSessions();
  }, [fetchSessions]);


  const fetchActiveLeadsCount = useCallback(async () => {
    try {
      const data = await api.get<{ count: number }>(
        "/admin/ai-chat-sessions/active-count",
      );
      setActiveLeadsCount(data?.count ?? 0);
    } catch {
    }
  }, []);

  useEffect(() => {
    void fetchActiveLeadsCount();
  }, [fetchActiveLeadsCount]);

  // Auto-refresh the list + active leads count every 5s so new sessions and
  // status changes (e.g. from another admin) show up without a manual reload.
  useEffect(() => {
    const interval = setInterval(() => {
      void fetchSessions(true);
      void fetchActiveLeadsCount();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchSessions, fetchActiveLeadsCount]);

  // Load the CSM list once — powers the assignee dropdowns and config panel.
  // Sessions can only be assigned to CSMs, so this is filtered at the source.
  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get<{ data: AdminUser[] }>("/admin/admins", {
          limit: "100",
          status: "active",
          role: "csm",
        });
        setAdmins(res?.data ?? []);
      } catch {
        setAdmins([]);
      }
    })();
  }, []);

  const toggleSort = (column: Exclude<SortBy, "activity">) => {
    setPage(1);
    if (sortBy === column) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setOrder("asc");
    }
  };

  const handleAssigneeChange = async (
    session: AiChatSession,
    assigneeId: string,
  ) => {
    const next = assigneeId || null;
    setSavingAssigneeId(session.id);
    try {
      await api.patch(`/admin/ai-chat-sessions/${session.id}/assignee`, {
        assigneeId: next,
      });
      const assignee = next
        ? (() => {
          const a = admins.find((x) => x.id === next);
          return a ? { id: a.id, name: adminLabel(a) } : null;
        })()
        : null;
      setSessions((prev) =>
        prev.map((s) =>
          s.id === session.id ? { ...s, assigneeId: next, assignee } : s,
        ),
      );
    } catch {
      // silently fail — user can retry
    } finally {
      setSavingAssigneeId(null);
    }
  };

  // Editable by a super admin, or by the CSM this lead is currently
  // assigned to. Mirrors the backend check in setSessionManualStage —
  // the UI restriction is a convenience, not the security boundary.
  const canEditManualStage = (session: AiChatSession) =>
    isSuperAdmin || (isCsm && !!user && session.assigneeId === user.id);

  const handleManualStageChange = async (
    session: AiChatSession,
    manualChatStage: ChatStage,
  ) => {
    if (
      manualChatStage === "ARCHIVED" &&
      !window.confirm(
        "Set C Stage to C0 (Archived)? This marks the lead as dead / inactive / not interested and removes it from this list. Are you sure?",
      )
    ) {
      return;
    }
    if (
      session.manualChatStage === "ARCHIVED" &&
      manualChatStage !== "ARCHIVED" &&
      !window.confirm(
        "Reactivate this lead from C0 (Archived)? It will move back into the active leads list. Are you sure?",
      )
    ) {
      return;
    }
    setSavingManualStageId(session.id);
    try {
      await api.patch(`/admin/ai-chat-sessions/${session.id}/manual-stage`, {
        manualChatStage,
      });
      // The default view excludes archived leads; the archived view shows
      // only them. If this change moves the session out of whichever view
      // is active, drop the row locally instead of waiting for a refetch.
      const wasArchived =
        session.stage === "ARCHIVED" || session.manualChatStage === "ARCHIVED";
      const isNowArchived =
        session.stage === "ARCHIVED" || manualChatStage === "ARCHIVED";
      if (isNowArchived !== showArchived) {
        setSessions((prev) => prev.filter((s) => s.id !== session.id));
        setTotal((prev) => Math.max(0, prev - 1));
      } else {
        setSessions((prev) =>
          prev.map((s) =>
            s.id === session.id ? { ...s, manualChatStage } : s,
          ),
        );
      }
      if (wasArchived !== isNowArchived) {
        void fetchActiveLeadsCount();
      }
    } catch {
      // silently fail — user can retry
    } finally {
      setSavingManualStageId(null);
    }
  };

  const handleToggleManualMode = async (session: AiChatSession, enable: boolean) => {
    setTogglingId(session.id);
    try {
      await api.patch(`/admin/ai-chat-sessions/${session.id}/manual-mode`, { enable });
      setSessions((prev) =>
        prev.map((s) => (s.id === session.id ? { ...s, isManualMode: enable } : s)),
      );
      if (selectedSession?.id === session.id) {
        setSelectedSession((prev) => prev ? { ...prev, isManualMode: enable } : prev);
      }
    } catch {
      // silently fail — user can retry
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleBlocked = async (session: AiChatSession, blocked: boolean) => {
    let reason: string | undefined;
    if (blocked) {
      // Confirmed because this silences a real person and, unlike manual mode,
      // nothing times out and restores them — only an admin can undo it.
      const input = window.prompt(
        `Block this chat?\n\nNo one will reply — the AI stays silent, admin messages are refused, and re-engagement templates skip them. Their incoming messages are still recorded.\n\nReason (optional):`,
      );
      if (input === null) return;
      reason = input.trim() || undefined;
    } else if (!window.confirm("Unblock this chat? The AI will start replying again.")) {
      return;
    }

    setTogglingId(session.id);
    try {
      await api.patch(`/admin/ai-chat-sessions/${session.id}/block`, { blocked, reason });
      const patch = { isBlocked: blocked, blockReason: blocked ? reason ?? null : null };
      setSessions((prev) =>
        prev.map((s) =>
          s.id === session.id
            ? { ...s, ...patch, isManualMode: blocked ? false : s.isManualMode }
            : s,
        ),
      );
      if (selectedSession?.id === session.id) {
        setSelectedSession((prev) =>
          prev ? { ...prev, ...patch, isManualMode: blocked ? false : prev.isManualMode } : prev,
        );
      }
    } catch {
      // silently fail — user can retry
    } finally {
      setTogglingId(null);
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex flex-wrap items-center gap-x-2 gap-y-1">
            <Bot className="h-6 w-6 shrink-0 text-primary" />
            AI Chat Sessions
            {activeLeadsCount !== null && (
              <span className="text-sm sm:text-base font-medium text-gray-400">
                ({activeLeadsCount} Active Leads)
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitor and take over AI counselor conversations
          </p>
        </div>
        {/* Wraps rather than overflowing once the sidebar is gone on mobile. */}
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {isSuperAdmin && (
            <button
              onClick={() => setShowConfig(true)}
              title="Allocation"
              className="flex items-center gap-2 px-2.5 sm:px-3 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Settings className="h-5 w-5" />
              <span className="hidden sm:inline">Allocation</span>
            </button>
          )}
          <button
            onClick={() => {
              setShowArchived((v) => !v);
              setPage(1);
            }}
            title={showArchived ? "Active" : "Archived"}
            className={cn(
              "flex items-center gap-2 px-2.5 sm:px-3 py-2.5 text-sm border rounded-md",
              showArchived
                ? "text-primary border-primary bg-primary/5"
                : "text-gray-600 border-gray-300 hover:bg-gray-50",
            )}
          >
            <Archive className="h-5 w-5" />
            <span className="hidden sm:inline">{showArchived ? "Active" : "Archived"}</span>
          </button>
          <button
            onClick={() => {
              void fetchSessions();
              void fetchActiveLeadsCount();
            }}
            disabled={loading}
            title="Refresh (auto-refreshes every 5s)"
            aria-label="Refresh"
            className="flex items-center justify-center p-2.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={cn("h-5 w-5", loading && "animate-spin")} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative lg:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by student name or phone..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        {/* Two-up on phones so four dropdowns don't become a tall stack. */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
          <select
            value={filterManual}
            onChange={(e) => {
              setFilterManual(e.target.value as "all" | "manual" | "ai");
              setPage(1);
            }}
            className="min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All sessions</option>
            <option value="manual">Manual mode ON</option>
            <option value="ai">AI mode (normal)</option>
          </select>
          {!isCsm && (
            <select
              value={filterAssignee}
              onChange={(e) => {
                setFilterAssignee(e.target.value);
                setPage(1);
              }}
              className="min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary sm:order-last"
            >
              <option value="all">All assignees</option>
              <option value="unassigned">Unassigned</option>
              {admins.map((a) => (
                <option key={a.id} value={a.id}>
                  {adminLabel(a)}
                </option>
              ))}
            </select>
          )}
          <div className="col-span-2 flex items-center gap-1">
            <select
              value={filterAStage}
              onChange={(e) => {
                setFilterAStage(e.target.value as "all" | ChatStage);
                setPage(1);
              }}
              className="min-w-0 flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All A Stages</option>
              {(showArchived ? MANUAL_STAGE_OPTIONS : STAGE_ORDER).map((s) => (
                <option key={s} value={s} title={stageLabel(s)}>
                  {aStageLabel(s)}
                </option>
              ))}
            </select>
            <select
              value={filterCStage}
              onChange={(e) => {
                setFilterCStage(e.target.value as "all" | ChatStage);
                setPage(1);
              }}
              className="min-w-0 flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All C Stages</option>
              {(showArchived ? MANUAL_STAGE_OPTIONS : STAGE_ORDER).map((s) => (
                <option key={s} value={s} title={stageLabel(s)}>
                  {cStageLabel(s)}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowStageInfo((v) => !v)}
              title="What do the stages mean?"
              aria-label="What do the stages mean?"
              className={cn(
                "shrink-0 p-2 rounded-md border border-gray-300 hover:bg-gray-50",
                showStageInfo ? "text-primary" : "text-gray-500",
              )}
            >
              <Info className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Stage legend */}
      {showStageInfo && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Conversation stages
            </h3>
            <button
              onClick={() => setShowStageInfo(false)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mb-3 text-xs text-gray-500">
            A daily AI analysis classifies each WhatsApp conversation into the
            stage that best reflects where the student currently is.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex shrink-0 px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                A0/C0: ARCHIVED
              </span>
              <p className="text-xs text-gray-600">
                Dead / inactive / not interested lead.
              </p>
            </div>
            {STAGE_ORDER.map((s) => (
              <div key={s} className="flex items-start gap-2">
                <span
                  className={cn(
                    "mt-0.5 inline-flex shrink-0 px-2 py-1 rounded-full text-xs font-medium",
                    STAGE_META[s].className,
                  )}
                >
                  {aStageLabel(s)}/{cStageLabel(s)}: {s.replace(/_/g, " ")}
                </span>
                <p className="text-xs text-gray-600">
                  {STAGE_META[s].description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <MessageCircle className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No AI chat sessions found</p>
          </div>
        ) : (
          <>
          {/*
            Mobile: one card per session. A horizontally scrolling 8-column
            table is unusable on a phone — the columns that matter (stage,
            assignee, manual mode) all sit off-screen — so the same controls
            are stacked and labelled here instead.
          */}
          <ul className="divide-y divide-gray-200 md:hidden">
            {sessions.map((session) => (
              <li
                key={session.id}
                onClick={() => setSelectedSession(session)}
                className="p-4 space-y-3 active:bg-gray-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {session.student?.name ?? "Unknown"}
                    </p>
                    {session.student?.phoneNumber && (
                      <p className="text-xs text-gray-500 truncate">
                        {session.student.phoneNumber}
                      </p>
                    )}
                  </div>
                  <span
                    className={cn(
                      "inline-flex shrink-0 px-2 py-1 rounded-full text-xs font-medium",
                      session.status === "active"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-600",
                    )}
                  >
                    {session.status}
                  </span>
                </div>

                <div>
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {session.latestMessage?.message ?? (
                      <span className="italic text-gray-400">No messages yet</span>
                    )}
                  </p>
                  {session.latestMessage?.sentAt && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatTime(session.latestMessage.sentAt)}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">A</span>
                    {session.stage ? (
                      <span
                        title={stageLabel(session.stage)}
                        className={cn(
                          "inline-flex px-2 py-1 rounded-full text-xs font-medium",
                          STAGE_META[session.stage].className,
                        )}
                      >
                        {aStageLabel(session.stage)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">C</span>
                    {canEditManualStage(session) ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        <select
                          value={session.manualChatStage ?? ""}
                          disabled={savingManualStageId === session.id}
                          onChange={(e) =>
                            void handleManualStageChange(
                              session,
                              e.target.value as ChatStage,
                            )
                          }
                          className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                        >
                          {MANUAL_STAGE_OPTIONS.map((s) => (
                            <option key={s} value={s} title={stageLabel(s)}>
                              {cStageLabel(s)}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : session.manualChatStage ? (
                      <span
                        title={stageLabel(session.manualChatStage)}
                        className={cn(
                          "inline-flex px-2 py-1 rounded-full text-xs font-medium",
                          STAGE_META[session.manualChatStage].className,
                        )}
                      >
                        {cStageLabel(session.manualChatStage)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </div>

                  <div
                    className="flex items-center gap-2 ml-auto"
                    onClick={(e) => e.stopPropagation()}
                    title={
                      isOutside24hWindow(session)
                        ? "Outside the 24h WhatsApp window — the student's last message was over 24h ago, so free-form messages (manual or AI) can't be sent until they message again."
                        : undefined
                    }
                  >
                    <ToggleSwitch
                      checked={session.isManualMode}
                      onChange={(val) => void handleToggleManualMode(session, val)}
                      disabled={
                        togglingId === session.id ||
                        isOutside24hWindow(session) ||
                        !!session.isBlocked
                      }
                    />
                    <span className="text-xs text-gray-500">
                      {session.isBlocked
                        ? "Blocked"
                        : session.isManualMode
                          ? "Manual"
                          : "AI"}
                    </span>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleToggleBlocked(session, !session.isBlocked);
                    }}
                    disabled={togglingId === session.id}
                    title={
                      session.isBlocked
                        ? session.blockReason
                          ? `Blocked — ${session.blockReason}. Tap to unblock.`
                          : "Blocked. Tap to unblock."
                        : "Block this chat — nobody will reply"
                    }
                    aria-label={session.isBlocked ? "Unblock" : "Block"}
                    className={cn(
                      "inline-flex items-center justify-center p-1.5 rounded-md border transition-colors disabled:opacity-50 shrink-0",
                      session.isBlocked
                        ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                        : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
                    )}
                  >
                    {session.isBlocked ? (
                      <ShieldCheck className="h-3.5 w-3.5" />
                    ) : (
                      <Ban className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>

                {!isCsm && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 shrink-0">Assignee</span>
                    {isSuperAdmin ? (
                      <div className="min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={session.assigneeId ?? ""}
                          disabled={savingAssigneeId === session.id}
                          onChange={(e) =>
                            void handleAssigneeChange(session, e.target.value)
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                        >
                          <option value="">Unassigned</option>
                          {admins.map((a) => (
                            <option key={a.id} value={a.id}>
                              {adminLabel(a)}
                            </option>
                          ))}
                          {/* Keep the current assignee visible even if not in the active pool */}
                          {session.assignee &&
                            !admins.some((a) => a.id === session.assignee!.id) && (
                              <option value={session.assignee.id}>
                                {session.assignee.name}
                              </option>
                            )}
                        </select>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-600 truncate">
                        {session.assignee?.name ?? "Unassigned"}
                      </span>
                    )}
                  </div>
                )}

                <p className="text-xs text-gray-400">
                  Started {formatTime(session.startedAt)}
                </p>
              </li>
            ))}
          </ul>

          {/* Desktop / tablet: the full table */}
          <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/*
                  Frozen identity column. Eight columns don't fit even on a
                  1280px laptop once the sidebar is out, so the table always
                  scrolls horizontally — pinning Student keeps every row
                  attributable to a person while the rest scrolls under it.
                */}
                <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                  Student
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Message
                </th>
                <th
                  onClick={() => toggleSort("stage")}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                >
                  <span className="inline-flex items-center gap-1">
                    A Stage
                    {sortBy === "stage" &&
                      (order === "asc" ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      ))}
                  </span>
                </th>
                <th
                  onClick={() => toggleSort("manualStage")}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                >
                  <span className="inline-flex items-center gap-1">
                    C Stage
                    {sortBy === "manualStage" &&
                      (order === "asc" ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      ))}
                  </span>
                </th>
                {!isCsm && (
                  <th
                    onClick={() => toggleSort("assignee")}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                  >
                    <span className="inline-flex items-center gap-1">
                      Assignee
                      {sortBy === "assignee" &&
                        (order === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        ))}
                    </span>
                  </th>
                )}
                {/*
                  Column priority: Status and Started are read-only and the
                  least acted-on, so they drop first at narrower widths rather
                  than pushing the editable columns off-screen. Both are still
                  in the mobile cards and the row's detail modal.
                */}
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Manual Mode
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Block
                </th>
                <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.map((session) => (
                <tr
                  key={session.id}
                  onClick={() => setSelectedSession(session)}
                  className="group hover:bg-gray-50 cursor-pointer"
                >
                  {/* Sticky cells paint their own background, so the row hover
                      has to be re-applied here or this column stays white. */}
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 px-4 py-4 whitespace-nowrap shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                    <div className="text-sm font-medium text-gray-900">
                      {session.student?.name ?? "Unknown"}
                    </div>
                    {session.student?.phoneNumber && (
                      <div className="text-xs text-gray-500">
                        {session.student.phoneNumber}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-gray-700 truncate max-w-[12rem]">
                      {session.latestMessage?.message ?? (
                        <span className="italic text-gray-400">No messages yet</span>
                      )}
                    </p>
                    {session.latestMessage?.sentAt && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatTime(session.latestMessage.sentAt)}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {session.stage ? (
                      <span
                        title={stageLabel(session.stage)}
                        className={cn(
                          "inline-flex px-2 py-1 rounded-full text-xs font-medium",
                          STAGE_META[session.stage].className,
                        )}
                      >
                        {aStageLabel(session.stage)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {canEditManualStage(session) ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        <select
                          value={session.manualChatStage ?? ""}
                          disabled={savingManualStageId === session.id}
                          onChange={(e) =>
                            void handleManualStageChange(
                              session,
                              e.target.value as ChatStage,
                            )
                          }
                          className="px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                        >
                          {MANUAL_STAGE_OPTIONS.map((s) => (
                            <option key={s} value={s} title={stageLabel(s)}>
                              {cStageLabel(s)}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : session.manualChatStage ? (
                      <span
                        title={stageLabel(session.manualChatStage)}
                        className={cn(
                          "inline-flex px-2 py-1 rounded-full text-xs font-medium",
                          STAGE_META[session.manualChatStage].className,
                        )}
                      >
                        {cStageLabel(session.manualChatStage)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  {!isCsm && (
                    <td className="px-4 py-4 whitespace-nowrap">
                      {isSuperAdmin ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <select
                            value={session.assigneeId ?? ""}
                            disabled={savingAssigneeId === session.id}
                            onChange={(e) =>
                              void handleAssigneeChange(session, e.target.value)
                            }
                            className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                          >
                            <option value="">Unassigned</option>
                            {admins.map((a) => (
                              <option key={a.id} value={a.id}>
                                {adminLabel(a)}
                              </option>
                            ))}
                            {/* Keep the current assignee visible even if not in the active pool */}
                            {session.assignee &&
                              !admins.some((a) => a.id === session.assignee!.id) && (
                                <option value={session.assignee.id}>
                                  {session.assignee.name}
                                </option>
                              )}
                          </select>
                        </div>
                      ) : (
                        // Non-super-admin, non-CSM admins can see who's assigned but not reassign.
                        <span className="text-sm text-gray-600">
                          {session.assignee?.name ?? "Unassigned"}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="hidden lg:table-cell px-4 py-4 whitespace-nowrap">
                    <span
                      className={cn(
                        "inline-flex px-2 py-1 rounded-full text-xs font-medium",
                        session.status === "active"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600",
                      )}
                    >
                      {session.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div
                      className="flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                      title={
                        isOutside24hWindow(session)
                          ? "Outside the 24h WhatsApp window — the student's last message was over 24h ago, so free-form messages (manual or AI) can't be sent until they message again."
                          : undefined
                      }
                    >
                      <ToggleSwitch
                        checked={session.isManualMode}
                        onChange={(val) => void handleToggleManualMode(session, val)}
                        disabled={
                          togglingId === session.id ||
                          isOutside24hWindow(session) ||
                          !!session.isBlocked
                        }
                      />
                      <span className="text-xs text-gray-500">
                        {session.isBlocked
                          ? "Blocked"
                          : session.isManualMode
                            ? "Manual"
                            : "AI"}
                      </span>
                    </div>
                  </td>
                  <td
                    className="px-4 py-4 whitespace-nowrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => void handleToggleBlocked(session, !session.isBlocked)}
                      disabled={togglingId === session.id}
                      title={
                        session.isBlocked
                          ? session.blockReason
                            ? `Blocked — ${session.blockReason}. Click to unblock.`
                            : "Blocked. Click to unblock."
                          : "Block this chat — nobody will reply"
                      }
                      aria-label={session.isBlocked ? "Unblock" : "Block"}
                      className={cn(
                        "inline-flex items-center justify-center p-1.5 rounded-md border transition-colors disabled:opacity-50",
                        session.isBlocked
                          ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                          : "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
                      )}
                    >
                      {session.isBlocked ? (
                        <ShieldCheck className="h-3.5 w-3.5" />
                      ) : (
                        <Ban className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </td>
                  <td className="hidden xl:table-cell px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTime(session.startedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>
        )}

        {/* Pagination */}
        {!loading && (
          <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              total={total}
              pageSize={PAGE_SIZE}
              itemLabel="sessions"
            />
          </div>
        )}
      </div>

      {/* Chat modal */}
      {selectedSession && (
        <AiSessionChatModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          onManualModeChange={(id, val) => {
            void handleToggleManualMode({ ...selectedSession, id } as AiChatSession, val);
          }}
        />
      )}

      {/* Assignment config panel */}
      {showConfig && (
        <AssignmentConfigModal
          admins={admins}
          onClose={() => setShowConfig(false)}
        />
      )}
    </div>
  );
};

interface AssignmentConfigModalProps {
  admins: AdminUser[];
  onClose: () => void;
}


const AssignmentConfigModal: React.FC<AssignmentConfigModalProps> = ({
  admins,
  onClose,
}) => {
  const [excluded, setExcluded] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ excludedCsmIds: string[] }>(
          "/admin/whatsapp-assignment-settings",
        );
        setExcluded(
          Array.isArray(data?.excludedCsmIds) ? data.excludedCsmIds : [],
        );
      } catch {
        // leave defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/whatsapp-assignment-settings", {
        excludedCsmIds: excluded,
      });
      onClose();
    } catch {
      // silently fail — user can retry
    } finally {
      setSaving(false);
    }
  };

  const options = admins.map((a) => ({ value: a.id, label: adminLabel(a) }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-4 sm:px-6">
          <h2 className="text-lg font-semibold text-gray-900">
            WhatsApp Assignment Config
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:px-6">
            <div>
              <p className="mb-1 text-sm font-medium text-gray-900">
                Excluded from rotation
              </p>
              <p className="mb-2 text-xs text-gray-500">
                New WhatsApp leads always round-robin across every active
                CSM — pick anyone here to temporarily pause new leads going
                to them (e.g. while on leave). New CSMs are included by
                default.
              </p>
              <MultiSelect
                options={options}
                selected={excluded}
                onChange={setExcluded}
                placeholder="Select CSMs to exclude..."
              />
            </div>
          </div>
        )}

        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 px-4 py-4 sm:px-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving || loading}
            className="px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiSessions;
